import io, { Socket } from "socket.io-client";
import SimplePeer from "simple-peer";
import { refreshAccessToken } from "../utils/auth";

export class StreamService {
  private socket: typeof Socket;
  private peer: SimplePeer.Instance | null = null;
  private stream: MediaStream | null = null;

  constructor() {
    const wsUrl =
      process.env.NODE_ENV === "production"
        ? "wss://api.codecoast.org/live-classes"
        : "ws://localhost:3030/live-classes";

    console.log("Connecting to WebSocket URL:", wsUrl);

    this.socket = io(wsUrl, {
      transports: ["websocket", "polling"],
      auth: {
        token: localStorage.getItem("access_token"),
      },
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
    });

    // Log connection events for debugging
    this.socket.on("connect", () => {
      console.log("=== WEBSOCKET CONNECTED ===");
      console.log("Socket connected successfully");
      console.log("Socket ID:", this.socket.id);
    });

    this.socket.on("connect_error", async (error: any) => {
      console.error("=== WEBSOCKET CONNECTION ERROR ===");
      console.error("Socket connection error:", error);
      console.error("Error message:", error.message);
      console.error("Error type:", error.type);
      
      // If it's an authentication error, try to refresh the token
      if (error.message?.includes("jwt expired") || error.message?.includes("Unauthorized")) {
        console.log("Attempting to refresh token for WebSocket connection...");
        try {
          const newToken = await refreshAccessToken();
          console.log("Token refreshed, reconnecting WebSocket...");
          
          // Disconnect and reconnect with new token
          this.socket.disconnect();
          // Create a new socket instance with the new token
          this.socket = io(wsUrl, {
            transports: ["websocket", "polling"],
            auth: {
              token: newToken,
            },
            forceNew: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            timeout: 10000,
          });
          this.setupSocketListeners();
          return;
        } catch (refreshError) {
          console.error("Failed to refresh token:", refreshError);
        }
      }
      
      // Emit a custom event for the UI to handle
      this.socket.emit("connection-error", { error: error.message });
    });

    this.socket.on("disconnect", (reason: string) => {
      console.log("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        // Server disconnected, try to reconnect
        this.socket.connect();
      }
    });

    this.initializePeer();
    this.setupSocketListeners();
    this.setupTokenRefresh();
  }

  private initializePeer(initiator: boolean = false) {
    // console.log("initializePeer:::::", this.stream?.getTracks());
    this.peer = new SimplePeer({
      initiator,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun.twilio.com:3478" },
        ],
      },
      stream: this.stream || undefined,
      trickle: true,
    });

    this.setupPeerListeners();
  }

  private setupPeerListeners() {
    if (!this.peer) return;

    this.peer.on("signal", (data) => {
      // Send signaling data to remote peer via socket
      this.socket.emit("signal", data);
    });

    this.peer.on("connect", () => {
      console.log("Peer connection established");
    });

    this.peer.on("stream", (stream: MediaStream) => {
      console.log("Received remote stream");
      // Handle incoming stream (e.g., attach to video element)
      this.socket.emit("instructor-stream", stream);
    });

    this.peer.on("error", (err: Error) => {
      console.error("Detailed peer error:", {
        message: err.message,
        name: err.name,
        stack: err.stack,
      });

      // Attempt to reconnect on error
      this.reconnectPeer();
    });
  }

  private reconnectPeer() {
    console.log("Attempting to reconnect peer...");
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    setTimeout(() => {
      this.initializePeer(true);
    }, 1000);
  }

  private setupSocketListeners() {
    this.socket.on("signal", (data: any) => {
      if (!this.peer) {
        this.initializePeer(false);
      }
      this.peer?.signal(data);
    });

    this.socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    this.socket.on("connect", () => {
      console.log("Socket connected");
    });
  }

  public async startStreaming(
    stream: MediaStream,
    roomId: string,
    userId: string,
    name: string
  ): Promise<void> {
    try {
      this.stream = stream;
      // Initialize as initiator since this is the instructor
      this.initializePeer(true);

      // Join room
      this.socket.emit("join-room", {
        roomId,
        userId,
        role: "admin",
        name,
      });

      // Listen for student connection requests
      this.socket.on("request-stream", (data: any) => {
        console.log("Stream requested by:", data.userId);
        // Ensure peer is initialized with the stream
        if (!this.peer) {
          this.initializePeer(true);
        }
      });
    } catch (error) {
      console.error("Streaming error:", error);
      throw error;
    }
  }

  public stopStreaming() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    this.socket.disconnect();
  }

  public get socketInstance(): typeof Socket {
    return this.socket;
  }

  public leaveRoom(roomId: string, userId: string) {
    if (this.socketInstance) {
      this.socketInstance.emit("leave-room", { roomId, userId });
      this.socketInstance.disconnect();
    }
  }

  public async joinRoom(
    roomId: string,
    userId: string,
    role: string,
    name: string
  ) {
    if (this.socketInstance) {
      this.socketInstance.emit("join-room", { roomId, userId, role, name });
    }
  }

  public async toggleAudio(enabled: boolean): Promise<void> {
    if (this.stream) {
      const audioTrack = this.stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = enabled;
      }
    }
  }

  public async toggleVideo(enabled: boolean): Promise<void> {
    if (this.stream) {
      const videoTrack = this.stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = enabled;
      }
    }
  }

  public get peerInstance(): SimplePeer.Instance | null {
    return this.peer;
  }

  public initializeNewPeer(
    initiator: boolean = false,
    stream?: MediaStream
  ): void {
    if (this.peerInstance) {
      this.peerInstance.destroy();
    }

    this.peer = new SimplePeer({
      initiator,
      stream,
      trickle: true,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:global.stun.twilio.com:3478" },
        ],
      },
    });

    // Set up common peer event handlers
    this.peer.on("error", (err) => {
      console.error("Peer error:", err);
    });

    this.peer.on("connect", () => {
      console.log("Peer connected");
    });

    this.peer.on("close", () => {
      console.log("Peer connection closed");
    });
  }

  getSocketInstance() {
    return this.socket;
  }

  async refreshConnection() {
    try {
      const newToken = await refreshAccessToken();
      console.log("Refreshing WebSocket connection with new token...");
      
      // Disconnect and reconnect with new token
      this.socket.disconnect();
      // Create a new socket instance with the new token
      const wsUrl =
        process.env.NODE_ENV === "production"
          ? "wss://api.codecoast.org/live-classes"
          : "ws://localhost:3031/live-classes";
      
      this.socket = io(wsUrl, {
        transports: ["websocket", "polling"],
        auth: {
          token: newToken,
        },
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
      });
      this.setupSocketListeners();
      
      return true;
    } catch (error) {
      console.error("Failed to refresh WebSocket connection:", error);
      return false;
    }
  }

  private setupTokenRefresh() {
    // Check token expiration every 5 minutes
    setInterval(async () => {
      try {
        const currentToken = localStorage.getItem("access_token");
        if (!currentToken) return;

        // Decode token to check expiration
        const tokenData = JSON.parse(atob(currentToken.split(".")[1]));
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds

        // If token will expire in next 2 minutes, refresh it
        if (expirationTime - Date.now() < 120000) {
          console.log("Token will expire soon, refreshing WebSocket connection...");
          await this.refreshConnection();
        }
      } catch (error) {
        console.error("Error checking token expiration:", error);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }
}
